// A TÉTELEK STÁTUSZ SZERINT — a Bevétel-elemzés szűrő-sávjának jobb oldala (a korábbi aktív / függőben / összes
// badge-hármas utódja, user-kérés).
//
// MIÉRT ÍGY:
//  • az „összes" helyett INAKTÍV: a három szám a hónapban érvényes tételek MEGOSZLÁSA — az „összes" ebből semmit
//    nem mondott, az inaktív (a záró dátumáig még számlázódó) viszont igen;
//  • a nézet-kapcsoló (`Segmented`) dizájnja, színes ponttal: egy pillantásra olvasható, és GOMB — egy elemre
//    kattintva a lap arra a státuszra szűr (ismételt kattintás: vissza az alapra, az aktívra); a kiválasztott elem
//    kiemelt, ugyanúgy, mint a nézet-kapcsolón;
//  • a FÜGGŐBEN elem végén a szem-ikon: a függő tételek listája (ami korábban külön ült a badge-ek között);
//  • az INAKTÍV, LEZÁRATLAN elem NEM szűr, hanem a tételek listáját nyitja (user-döntés): ezek nem számlázódnak
//    (nyitott / jövőbeli záró dátum, maga a sor vagy az ügyfele / fiókja inaktív), a terv-oldal rájuk szűrve
//    üres lenne — a szám adat-rendbetételi jel, a lista a teendő.

import { useTranslation } from 'react-i18next';

import { EyeButton } from '@/features/revenue-forecast/components/details';
import { type ForecastStatus } from '@/features/revenue-forecast/use-revenue-filters';
import { cn } from '@/lib/utils';

const DOT: Record<ForecastStatus, string> = {
  active: 'bg-success',
  pending: 'bg-warning-400',
  inactive: 'bg-neutral-400',
};

export function StatusCounts({
  counts,
  selected,
  onSelect,
  onShowPending,
  onShowInactive,
}: {
  /** A három darabszám; az `inactive` egy régebbi BFF-válaszból hiányozhat — akkor az elem nem jelenik meg. */
  counts: { active: number; pending: number; inactive: number | undefined };
  /** A státusz-szűrő MOSTANI értéke — az egyetlen kiválasztott státusz eleme kiemelt. */
  selected: readonly ForecastStatus[];
  onSelect: (status: ForecastStatus) => void;
  /** A függő tételek listája (szem-ikon); `undefined` → nincs ikon (nincs mit listázni, vagy szűrt nézet). */
  onShowPending?: (() => void) | undefined;
  /**
   * Az INAKTÍV, LEZÁRATLAN tételek listája. Az elem kattintásra NEM szűr (a terv-oldal üres lenne — ezek nem
   * számlázódnak), hanem ezt a listát nyitja; 0 darabnál nincs mit mutatni, ezért nem kattintható.
   */
  onShowInactive: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const items: { status: ForecastStatus; count: number }[] = [
    { status: 'active', count: counts.active },
    { status: 'pending', count: counts.pending },
    ...(counts.inactive === undefined
      ? []
      : [{ status: 'inactive' as const, count: counts.inactive }]),
  ];

  return (
    <div
      role="group"
      aria-label={t('revenueForecast.counts.label')}
      className="bg-muted/40 flex w-fit flex-wrap items-center gap-1 rounded-lg p-1"
    >
      {items.map(({ status, count }) => {
        const active = selected.length === 1 && selected[0] === status;
        const dot = (
          <span className={cn('size-2 shrink-0 rounded-full', DOT[status])} aria-hidden="true" />
        );
        // INAKTÍV, LEZÁRATLAN: listát nyit, nem szűr (l. `onShowInactive`)
        if (status === 'inactive') {
          return (
            <button
              key={status}
              type="button"
              aria-haspopup="dialog"
              disabled={count === 0}
              title={t('revenueForecast.counts.inactiveHint')}
              onClick={onShowInactive}
              className="ease-atlas text-muted-foreground hover:bg-background/50 hover:text-foreground focus-visible:ring-accent-600 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 disabled:pointer-events-none"
            >
              {dot}
              {t('revenueForecast.counts.inactive')}
              <span className="text-foreground font-semibold tabular-nums">{count}</span>
            </button>
          );
        }
        return (
          <div
            key={status}
            className={cn(
              'ease-atlas inline-flex items-center rounded-md transition-colors duration-150',
              active ? 'bg-background shadow-sm' : 'hover:bg-background/50',
            )}
          >
            <button
              type="button"
              aria-pressed={active}
              title={t('revenueForecast.counts.filterHint')}
              onClick={() => onSelect(status)}
              className={cn(
                'focus-visible:ring-accent-600 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm focus-visible:outline-none focus-visible:ring-2',
                active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {dot}
              {t(`revenueForecast.counts.${status}`)}
              <span className="text-foreground font-semibold tabular-nums">{count}</span>
            </button>
            {status === 'pending' && onShowPending && (
              <span className="pr-1.5">
                <EyeButton
                  label={t('revenueForecast.details.pendingTitle')}
                  onClick={onShowPending}
                />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
